import React, { useContext as useContextCore, useState } from 'react'
import { useToggle } from 'react-use'
import { NavigationType, useLocation, useNavigationType } from 'react-router-dom'
import type { UseWhatsNewResult } from './useWhatsNew'
import { useWhatsNew } from './useWhatsNew'

export type WhatsNewContextType = Pick<UseWhatsNewResult, 'post' | 'canView'> & {
  open: boolean
  openWhatsNew: () => void
  /** Closes the dialog and stores the displayed post as the user's last seen one. */
  closeWhatsNew: () => Promise<void>
}

const context = React.createContext<WhatsNewContextType>({} as WhatsNewContextType)
const CoreProvider = context.Provider

export type WhatsNewProviderProps = {
  /** The host app's landing page; the dialog opens on it when the page loads, never on in-app navigation to it. */
  landingPath: string
  /** True for an app whose blog categories carry no tenant id (Payroll), so the tenant-free What's New category is read. */
  tenantFree?: boolean
  children: React.ReactNode
}

export const WhatsNewProvider = ({ landingPath, tenantFree = false, children }: WhatsNewProviderProps) => {
  const { pathname, key } = useLocation()
  const navigationType = useNavigationType()
  const { post, canView, unseen, loading, markSeen } = useWhatsNew({ tenantFree })
  const [deciding, toggleDeciding] = useToggle(true)
  const [open, toggleOpen] = useToggle(false)
  const [mountKey] = useState(key)

  // The mount's own redirects (a REPLACE, such as QuickScore's `/app` index route sending the page to Overview) keep
  // the decision pending; the first in-app PUSH cancels it, so navigating to the landing page never opens the dialog.
  const navigatedInApp = key !== mountKey && navigationType === NavigationType.Push

  // Decided once, when the data first resolves: a post published later never opens the dialog mid-session.
  if (deciding && (navigatedInApp || !loading)) {
    toggleDeciding(false)
    toggleOpen(!navigatedInApp && unseen && canView && pathname === landingPath)
  }

  const closeWhatsNew = async () => {
    toggleOpen(false)
    await markSeen(post._id)
  }

  return (
    <CoreProvider value={{ post, canView, open, openWhatsNew: () => toggleOpen(true), closeWhatsNew }}>
      {children}
    </CoreProvider>
  )
}

export const useWhatsNewContext = () => useContextCore(context)
